import { useTranslation } from "react-i18next";
import { useTheme } from "../../../context/ThemeContext";
import { buildThemeClasses } from "../theme";

// ---------------------------------------------------------------------------
// Control de presencia biométrico (facial/huella/NFC) y reloj checador (AgroCloud#83).
//
// ⚠️ ESTA SECCIÓN ES UNA MAQUETA HONESTA, Y LO DICE EN PANTALLA.
//
// La issue AgroCloud#83 está etiquetada `alcance-a-evaluar` y prioridad baja: requiere
// HARDWARE (lector facial/huella, tags NFC o un reloj checador físico) que aún
// no se ha evaluado ni comprado. Aquí NO hay:
//   • ninguna captura biométrica,
//   • ninguna integración con un reloj checador,
//   • ningún marcaje simulado que pretenda ser real.
//
// Lo único que existe es el contrato de datos (`TimeClockPunch` y `CLOCK_SOURCES`
// en `@torre/payroll`), para que el día que se apruebe el hardware el modelo ya
// esté. El backend rechaza con 501 cualquier marcaje cuya fuente no sea
// `manual`: fingir que acepta un marcaje facial sería mentirle al jefe de
// personal sobre una función de control de fraude que no existe.
//
// Mientras tanto, la asistencia se registra a mano en la sección Asistencia (AgroCloud#36).
// ---------------------------------------------------------------------------

/** Fuentes de marcaje del roadmap y su estado real. Ninguna implementada. */
const SOURCES_PLANNED = [
  { id: "face_biometric", icon: "ri-user-smile-line", labelKey: "payroll.clock.face" },
  { id: "fingerprint_biometric", icon: "ri-fingerprint-line", labelKey: "payroll.clock.fingerprint" },
  { id: "nfc", icon: "ri-rfid-line", labelKey: "payroll.clock.nfc" },
  { id: "time_clock", icon: "ri-time-line", labelKey: "payroll.clock.timeClock" },
];

export default function TimeClockSection() {
  const { t } = useTranslation("common");
  const { isDarkMode } = useTheme();
  const tc = buildThemeClasses(isDarkMode);

  return (
    <div className="h-full w-full overflow-y-auto p-6">
      <div className="mx-auto max-w-4xl">
        {/* Encabezado */}
        <div className="mb-6 flex items-center gap-3">
          <span className="flex h-11 w-11 items-center justify-center rounded-xl bg-primary-500/15 text-primary-500">
            <i className="ri-fingerprint-line text-xl" />
          </span>
          <div>
            <h1 className={`text-xl font-bold ${tc.text}`}>{t("payroll.menu.timeClock")}</h1>
            <p className={`text-sm ${tc.textSecondary}`}>{t("payroll.clockSubtitle")}</p>
          </div>
        </div>

        {/* El aviso que impide que nadie crea que esto ya funciona. */}
        <div className="mb-6 rounded-xl border border-amber-500/30 bg-amber-500/10 p-4">
          <div className="flex items-start gap-3">
            <i className="ri-tools-line mt-0.5 text-lg text-amber-500" />
            <div>
              <p className="text-sm font-semibold text-amber-500">
                {t("payroll.clock.comingSoonTitle")}
              </p>
              <p className="mt-1 text-sm text-amber-500/90">{t("payroll.clock.comingSoonBody")}</p>
            </div>
          </div>
        </div>

        {/* Lo que se evaluará — no una demo, una lista de intenciones. */}
        <div className={`mb-6 rounded-2xl border p-5 ${tc.cardBorder} ${tc.cardBg}`}>
          <h2 className={`mb-1 text-sm font-semibold ${tc.text}`}>
            {t("payroll.clock.planTitle")}
          </h2>
          <p className={`mb-4 text-xs ${tc.textSecondary}`}>{t("payroll.clock.planSubtitle")}</p>

          <div className="grid grid-cols-1 gap-3 sm:grid-cols-2">
            {SOURCES_PLANNED.map((source) => (
              <div
                key={source.id}
                className={`flex items-center justify-between gap-3 rounded-xl border p-3 ${tc.border}`}
              >
                <div className="flex items-center gap-3">
                  <i className={`${source.icon} text-lg ${tc.textSecondary}`} />
                  <span className={`text-sm ${tc.text}`}>{t(source.labelKey)}</span>
                </div>
                <span className="rounded-md bg-gray-400/15 px-2 py-0.5 text-[11px] font-medium text-gray-500">
                  {t("payroll.clock.notImplemented")}
                </span>
              </div>
            ))}
          </div>
        </div>

        {/* Qué hacer hoy, en vez de dejar al usuario en un callejón sin salida. */}
        <div className={`rounded-2xl border p-5 ${tc.cardBorder} ${tc.cardBg}`}>
          <h2 className={`mb-2 text-sm font-semibold ${tc.text}`}>
            {t("payroll.clock.meanwhileTitle")}
          </h2>
          <p className={`text-sm ${tc.textSecondary}`}>{t("payroll.clock.meanwhileBody")}</p>
        </div>
      </div>
    </div>
  );
}
